import GridViewIcon from '@src/svgs/GridViewIcon';
import ListViewIcon from '@src/svgs/ListViewIcon';
import styled from 'styled-components';

export type TranslationMemoryViewMode = 'table' | 'matrix';

interface Props {
  mode: TranslationMemoryViewMode;
  onChange: (mode: TranslationMemoryViewMode) => void;
}

/**
 * The Table | Matrix segmented control for the Translation Memory view
 * (PLAT-10847). Follows the segmented-control idiom, but with a neutral active
 * state — switching views is navigation, not a visual/creative control, so it
 * does not use the gold `--accent` (design-system.md).
 */
const ViewToggle = ({ mode, onChange }: Props) => (
  <Wrapper role="group" aria-label="Translation memory view">
    <button
      type="button"
      className={mode === 'table' ? 'active' : ''}
      aria-pressed={mode === 'table'}
      onClick={() => onChange('table')}
    >
      <ListViewIcon width="14" height="14" strokeColor="currentColor" />
      Table
    </button>
    <button
      type="button"
      className={mode === 'matrix' ? 'active' : ''}
      aria-pressed={mode === 'matrix'}
      onClick={() => onChange('matrix')}
    >
      <GridViewIcon width="14" height="14" strokeColor="currentColor" />
      Matrix
    </button>
  </Wrapper>
);

export default ViewToggle;

const Wrapper = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 0;
    background: transparent;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }

  button:hover:not(.active) {
    color: var(--foreground);
  }

  button.active {
    background: var(--background);
    color: var(--foreground);
  }
`;
